import { Component, Input, OnInit } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { ProjectService } from '../project.service';
import ProjectAccount from 'src/app/models/ProjectAccount';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProjectAccountModalComponent } from '../project-account-modal/project-account-modal.component';
import { GlobalFilesService } from 'src/app/shared/global-files/global-files.service';
import { LinkedFileToObject } from 'src/app/models/LinkedFileToObject';
import ChildInvoiceLink from 'src/app/models/ChildInvoiceLink';
import { LinkedObjectsToFileWithId } from 'src/app/models/LinkedObjectsToFileWithId';

@Component({
    selector: 'app-project-accounts-tab',
    templateUrl: './project-accounts-tab.component.html',
    styleUrls: ['./project-accounts-tab.component.scss']
})
export class ProjectAccountsTabComponent implements OnInit {
    @Input() project: any;
    accounts: ProjectAccount[] = [];
    showPastContracts = false;
    loading = false;
    incomingInvoicesAmount = 0;
    private invoiceAllocationByEmployeeId: Record<string, number> = {};

    constructor(
        private projectService: ProjectService,
        private logger: NGXLogger,
        private modalService: NgbModal,
        private globalFilesService: GlobalFilesService,
    ) {}

    ngOnInit(): void {
        this.refreshAccounts();
    }

    get displayedAccounts(): ProjectAccount[] {
        const sortedAccounts = this.sortAccountsByNewest(this.accounts || []);
        if (this.showPastContracts) {
            return sortedAccounts;
        }
        return sortedAccounts.filter((account) => !this.isPastContract(account));
    }

    get pastAccountsCount(): number {
        return (this.accounts || []).filter((account) => this.isPastContract(account)).length;
    }

    get hasPastContracts(): boolean {
        return this.pastAccountsCount > 0;
    }

    get activeAccountsCount(): number {
        return (this.accounts || []).filter((account) => !this.isPastContract(account)).length;
    }

    togglePastContracts(): void {
        this.showPastContracts = !this.showPastContracts;
    }

    getAccountDisplay(account: ProjectAccount): string {
        if (!account) {
            return '-';
        }

        const code = (account.accountCode || account.jiraAccountId || account.accountName || '').toString().trim();
        const title = (account.accountTitle || '').toString().trim();
        const fallbackName = (account.accountName || '').toString().trim();
        const readableTitle = title || (fallbackName && fallbackName !== code ? fallbackName : '');

        if (code && readableTitle && readableTitle !== code) {
            return `${code} (${readableTitle})`;
        }

        if (code && /^\d+$/.test(code)) {
            return readableTitle ? `${readableTitle} (${code})` : `Project account (${code})`;
        }

        return code || readableTitle || '-';
    }

    getBudgetProgress(account: ProjectAccount): number {
        const rawProgress = this.getBudgetProgressRaw(account);
        return Math.max(0, Math.min(100, rawProgress));
    }

    isBudgetLoading(account: ProjectAccount): boolean {
        return this.loading;
    }

    getBudgetProgressRaw(account: ProjectAccount): number {
        if (!account || account.budget === null || account.budget === undefined || account.yearlyActualCost == null) {
            return 0;
        }

        const budget = Number(account.budget);
        if (Number.isNaN(budget) || budget <= 0) {
            return 0;
        }

        const actual = this.getActualCostWithInvoices(account);
        const progress = (actual / budget) * 100;
        if (!Number.isFinite(progress) || Number.isNaN(progress) || progress < 0) {
            return 0;
        }
        return progress;
    }

    getBudgetedHours(account: ProjectAccount): number | null {
        if (!account || account.budget == null) {
            return null;
        }

        const budget = Number(account.budget);
        const hourlyRate = Number(account.price || 0);
        if (!Number.isFinite(budget) || budget < 0 || !Number.isFinite(hourlyRate) || hourlyRate <= 0) {
            return null;
        }

        return budget / hourlyRate;
    }

    getActualHours(account: ProjectAccount): number {
        const totalBillableSeconds = Number(account?.yearlyBillableSeconds || 0);
        if (!Number.isFinite(totalBillableSeconds) || totalBillableSeconds <= 0) {
            return 0;
        }

        return (totalBillableSeconds / 3600) * this.getWorklogMultiplier(account);
    }

    getHoursLeft(account: ProjectAccount): number | null {
        const budgetedHours = this.getBudgetedHours(account);
        if (budgetedHours == null) {
            return null;
        }

        return budgetedHours - this.getActualHours(account);
    }

    getActualCostWithInvoices(account: ProjectAccount): number {
        const base = Number(account?.yearlyActualCost || 0);
        const safeBase = Number.isFinite(base) && base > 0 ? base : 0;
        return safeBase * this.getWorklogMultiplier(account);
    }

    private getWorklogMultiplier(account: ProjectAccount): number {
        return account?.includeProjectManagement ? 1.15 : 1;
    }

    getBudgetByInvoiceProgressRaw(account: ProjectAccount): number {
        if (!account || account.budget === null || account.budget === undefined) {
            return 0;
        }

        const budget = Number(account.budget);
        if (Number.isNaN(budget) || budget <= 0) {
            return 0;
        }

        const progress = (this.getSalesInvoicesAmount(account) / budget) * 100;
        if (!Number.isFinite(progress) || Number.isNaN(progress) || progress < 0) {
            return 0;
        }
        return progress;
    }

    getBudgetByInvoiceProgress(account: ProjectAccount): number {
        const rawProgress = this.getBudgetByInvoiceProgressRaw(account);
        return Math.max(0, Math.min(100, rawProgress));
    }

    getSalesInvoicesAmount(account?: ProjectAccount): number {
        const employeeIds = this.getAccountEmployeeIds(account);
        if (!employeeIds.length) {
            return 0;
        }

        return employeeIds.reduce((sum, employeeId) => {
            const amount = Number(this.invoiceAllocationByEmployeeId[employeeId] || 0);
            if (!Number.isFinite(amount) || amount < 0) {
                return sum;
            }
            return sum + amount;
        }, 0);
    }

    getBudgetProgressTooltip(account: ProjectAccount): string {
        const budget = Number(account?.budget || 0);
        const actualWithoutInvoices = Number(account?.yearlyActualCost || 0);
        const actual = this.getActualCostWithInvoices(account);
        const totalBillableSeconds = Number(account?.yearlyBillableSeconds || 0);
        const totalBillableHours = totalBillableSeconds / 3600;
        const adjustedBillableHours = this.getActualHours(account);
        const worklogMultiplier = this.getWorklogMultiplier(account);
        const usage = this.getBudgetProgressRaw(account);
        const hourlyRate = Number(account?.price || 0);
        const { from, to } = this.getWorklogRangeForAccount(account);

        return [
            `Berekening: (jaarkost account / budget) x 100`,
            `Billable seconds: ${totalBillableSeconds.toFixed(0)} sec`,
            `Billable uren (bron): ${totalBillableHours.toFixed(2)} u`,
            `Billable uren (incl. PM): ${adjustedBillableHours.toFixed(2)} u`,
            `PM factor op worklogs: x ${worklogMultiplier.toFixed(2)}`,
            `Kostberekening uren: ${adjustedBillableHours.toFixed(2)} x € ${hourlyRate.toFixed(2)} = € ${actual.toFixed(2)}`,
            `Jaarkost account (worklogs): € ${actual.toFixed(2)}`,
            `Budget: € ${budget.toFixed(2)}`,
            `Resultaat: ${usage.toFixed(2)}%`,
            `Range worklogs: ${from} t.e.m. ${to}`,
        ].join('\n');
    }

    getBudgetByInvoiceTooltip(account: ProjectAccount): string {
        const budget = Number(account?.budget || 0);
        const sales = this.getSalesInvoicesAmount(account);
        const usage = this.getBudgetByInvoiceProgressRaw(account);

        return [
            `Berekening: (verkoopfacturen / budget) x 100`,
            `Verkoopfacturen (gelinkt aan dit account/contract): € ${sales.toFixed(2)}`,
            `Budget: € ${budget.toFixed(2)}`,
            `Resultaat: ${usage.toFixed(2)}%`,
        ].join('\n');
    }

    refreshAccounts() {
        if (!this.project || !this.project.uuid) return;
        console.log('project-accounts-tab: refreshAccounts called for project', this.project && this.project.uuid);
        this.loading = true;
        this.loadIncomingInvoicesAmount(this.project.uuid);
        this.projectService.getProjectAccounts(this.project.uuid).subscribe((accounts) => {
            this.accounts = this.normalizeAccounts(accounts || []);
            this.accounts.forEach((acc) => {
                // ensure UI shows uploaded contract even when backend omits hasContract
                acc.hasContract = !!acc.contractUrl;

                if (acc.contractUrl) {
                    // derive file name for display if backend didn't supply it
                    acc.contractFileName =
                        acc.contractUrl.split('/').pop()?.split('___')[0] || acc.contractFileName;
                }

                if (!acc.accountCode) {
                    acc.accountCode = acc.jiraAccountId || acc.accountName || null;
                }

                if (!acc.accountName) {
                    acc.accountName = acc.accountCode || acc.jiraAccountId;
                }

                if (!acc.accountTitle && acc.accountName && acc.accountName !== acc.accountCode) {
                    acc.accountTitle = acc.accountName;
                }
            });
            this.accounts = this.sortAccountsByNewest(this.accounts);
            this.loading = false;
        }, (err) => {
            this.logger.warn('Failed to load project accounts', err);
            this.loading = false;
        });
    }

    private getAccountEmployeeIds(account?: ProjectAccount): string[] {
        if (!account) {
            return [];
        }

        const ids = new Set<string>();
        (account.employeeIds || []).forEach((id) => {
            if (id) {
                ids.add(id);
            }
        });

        if (account.employeeId) {
            ids.add(account.employeeId);
        }

        return Array.from(ids);
    }

    private normalizeAccounts(accounts: ProjectAccount[]): ProjectAccount[] {
        const grouped = new Map<string, ProjectAccount>();

        for (const account of accounts || []) {
            const key = this.getAccountGroupingKey(account);
            const existing = grouped.get(key);

            if (!existing) {
                const base = new ProjectAccount({ ...account });
                const employeeIds = this.getAccountEmployeeIds(account);
                base.employeeIds = employeeIds;
                if (!base.employeeId && employeeIds.length > 0) {
                    base.employeeId = employeeIds[0];
                }
                grouped.set(key, base);
                continue;
            }

            const mergedEmployeeIds = new Set<string>([
                ...this.getAccountEmployeeIds(existing),
                ...this.getAccountEmployeeIds(account),
            ]);
            existing.employeeIds = Array.from(mergedEmployeeIds);
            if (!existing.employeeId && existing.employeeIds.length > 0) {
                existing.employeeId = existing.employeeIds[0];
            }
        }

        return Array.from(grouped.values());
    }

    private getAccountGroupingKey(account: ProjectAccount): string {
        const uuid = (account?.uuid || '').toString().trim();
        if (uuid) {
            return `uuid:${uuid.toLowerCase()}`;
        }

        const accountKey = (account?.jiraAccountId || account?.accountCode || account?.accountName || '').toString().trim().toLowerCase();
        const effectiveFrom = (account?.effectiveFrom || '').toString().trim().toLowerCase();
        const effectiveTo = (account?.effectiveTo || '').toString().trim().toLowerCase();
        const price = Number(account?.price || 0);
        const budget = Number(account?.budget || 0);
        const poNumber = (account?.poNumber || '').toString().trim().toLowerCase();
        const sesNumber = (account?.sesNumber || '').toString().trim().toLowerCase();
        const contractAgreementNumber = (account?.contractAgreementNumber || '').toString().trim().toLowerCase();
        const freeText = (account?.freeText || '').toString().trim().toLowerCase();
        const contractUrl = (account?.contractUrl || '').toString().trim().toLowerCase();

        return [
            accountKey,
            effectiveFrom,
            effectiveTo,
            price,
            budget,
            poNumber,
            sesNumber,
            contractAgreementNumber,
            freeText,
            contractUrl,
        ].join('|');
    }

    private loadIncomingInvoicesAmount(projectUuid: string): void {
        this.globalFilesService.getAllFilesLinkedToObject(projectUuid).subscribe(
            (payload) => {
                const linkedFiles = payload?.linkedFiles || [];
                this.incomingInvoicesAmount = this.sumIncomingLinkedInvoices(linkedFiles);
                this.loadInvoiceAllocationsByAccount(projectUuid, linkedFiles);
            },
            () => {
                this.incomingInvoicesAmount = 0;
                this.invoiceAllocationByEmployeeId = {};
            },
        );
    }

    private loadInvoiceAllocationsByAccount(projectUuid: string, linkedFiles: LinkedFileToObject[]): void {
        this.globalFilesService.getAllObjectsLinked().subscribe(
            (allLinkedObjects: LinkedObjectsToFileWithId[]) => {
                const linkedByFileId = (allLinkedObjects || []).reduce((acc: Record<string, LinkedObjectsToFileWithId>, item) => {
                    const key = (item?.yukiId || '').toString();
                    if (key) {
                        acc[key] = item;
                    }
                    return acc;
                }, {});

                const allocations: Record<string, number> = {};
                (linkedFiles || [])
                    .filter((file) => this.isIncomingInvoice(file))
                    .forEach((file) => {
                        const fileId = (file?.fileId || '').toString();
                        if (!fileId) return;

                        const linkedForFile = linkedByFileId[fileId];
                        const projectLink = (linkedForFile?.linkedObjects || []).find((link) => link?.objectId === projectUuid);
                        (projectLink?.childInvoiceLinks || []).forEach((child: ChildInvoiceLink) => {
                            const targetId = (child?.objectId || '').toString();
                            if (!targetId) return;

                            const objectType = (child?.objectType || '').toLowerCase();
                            if (objectType !== 'employee') return;

                            const amount = Math.abs(Number(child?.amount || 0));
                            if (!Number.isFinite(amount) || amount <= 0) return;
                            allocations[targetId] = (allocations[targetId] || 0) + amount;
                        });
                    });

                this.invoiceAllocationByEmployeeId = allocations;
            },
            () => {
                this.invoiceAllocationByEmployeeId = {};
            },
        );
    }

    private sumIncomingLinkedInvoices(files: LinkedFileToObject[]): number {
        return (files || [])
            .filter((file) => this.isIncomingInvoice(file))
            .reduce((sum, file) => sum + Math.abs(Number(file?.price || 0)), 0);
    }

    private isIncomingInvoice(file: LinkedFileToObject): boolean {
        const type = (file?.invoiceType || '').toLowerCase();
        const description = (file?.description || '').toLowerCase();
        const title = (file?.title || '').toLowerCase();
        const isArchivedOrDeleted =
            type.includes('archived') ||
            type.includes('deleted') ||
            description.includes('archived') ||
            description.includes('deleted') ||
            title.includes('archived') ||
            title.includes('deleted');

        return file?.folderId === 2 && !isArchivedOrDeleted;
    }

    private getWorklogRangeForAccount(account: ProjectAccount): { from: string; to: string } {
        if (account?.worklogFrom && account?.worklogTo) {
            return { from: account.worklogFrom, to: account.worklogTo };
        }

        const now = new Date();
        const defaultFrom = new Date(now.getFullYear(), 0, 1);
        const defaultTo = new Date(now.getFullYear(), 11, 31);

        const effectiveFromDate = this.parseIsoDate(account?.effectiveFrom) || this.parseIsoDate(account?.effectiveTo) || defaultFrom;
        const effectiveToDate = this.parseIsoDate(account?.effectiveTo) || this.parseIsoDate(account?.effectiveFrom) || defaultTo;

        const fromDate = new Date(effectiveFromDate.getFullYear(), effectiveFromDate.getMonth(), effectiveFromDate.getDate());
        const toDate = new Date(effectiveToDate.getFullYear(), effectiveToDate.getMonth(), effectiveToDate.getDate());

        if (fromDate.getTime() > toDate.getTime()) {
            return {
                from: this.formatDateForApi(toDate),
                to: this.formatDateForApi(fromDate),
            };
        }

        return {
            from: this.formatDateForApi(fromDate),
            to: this.formatDateForApi(toDate),
        };
    }

    private parseIsoDate(value?: string | null): Date | null {
        if (!value) {
            return null;
        }

        const isoPart = value.trim().split('T')[0];
        const parts = isoPart.split('-').map((part) => Number(part));
        if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
            return null;
        }

        const parsed = new Date(parts[0], parts[1] - 1, parts[2]);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }
        return parsed;
    }

    private isPastContract(account: ProjectAccount): boolean {
        const today = this.getDateWithoutTime(new Date());
        const fromDate = this.parseIsoDate(account?.effectiveFrom);
        const toDate = this.parseIsoDate(account?.effectiveTo);

        let normalizedFrom = fromDate;
        let normalizedTo = toDate;
        if (normalizedFrom && normalizedTo && normalizedFrom.getTime() > normalizedTo.getTime()) {
            const swappedFrom = normalizedTo;
            normalizedTo = normalizedFrom;
            normalizedFrom = swappedFrom;
        }

        return !!normalizedTo && this.getDateWithoutTime(normalizedTo).getTime() < today.getTime();
    }

    private sortAccountsByNewest(accounts: ProjectAccount[]): ProjectAccount[] {
        return [...(accounts || [])].sort((left, right) => {
            const leftValue = this.getAccountSortTimestamp(left);
            const rightValue = this.getAccountSortTimestamp(right);

            if (leftValue !== rightValue) {
                return rightValue - leftValue;
            }

            return this.getAccountDisplay(left).localeCompare(this.getAccountDisplay(right), undefined, { sensitivity: 'base' });
        });
    }

    private getAccountSortTimestamp(account: ProjectAccount): number {
        const primaryDate = this.parseIsoDate(account?.effectiveFrom);
        const fallbackDate = this.parseIsoDate(account?.effectiveTo);
        const selectedDate = primaryDate || fallbackDate;

        if (!selectedDate) {
            return 0;
        }

        return this.getDateWithoutTime(selectedDate).getTime();
    }

    private getDateWithoutTime(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private formatDateForApi(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    openProjectAccountModal() {
        const modalRef = this.modalService.open(ProjectAccountModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.projectUuid = this.project.uuid;
        modalRef.componentInstance.jiraProjectId = this.project.jiraProjectId || this.project.jiraProjectKey || null;
        modalRef.result.then((res) => {
            if (res === 'saved') {
                this.refreshAccounts();
            }
        }, () => {});
    }

    openEditProjectAccountModal(account: ProjectAccount) {
        const modalRef = this.modalService.open(ProjectAccountModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.projectUuid = this.project.uuid;
        modalRef.componentInstance.jiraProjectId = this.project.jiraProjectId || this.project.jiraProjectKey || null;
        modalRef.componentInstance.existingAccount = account;
        modalRef.result.then((res) => {
            if (res === 'saved') {
                this.refreshAccounts();
            }
        }, () => {});
    }

    removeProjectAccount(uuid: string) {
        this.projectService.deleteProjectAccount(uuid).subscribe(() => {
            this.refreshAccounts();
        }, (err) => {
            this.logger.warn('Failed to delete project account', err);
        });
    }
}
